import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Document, Operation, Partner } from 'types';
import { CustomRetention } from 'types/Operation';

import { getOperationsByIds } from 'modules/transactions/api/getOperationsByIds';

import { DividendData } from '../types/DividendData';

type UseGetDividendsProps = {
  partner: Partner;
};

type DataByOperation = Record<
  string,
  {
    totalAmount: number;
    retentionPercentage: number;
    customRetentions: CustomRetention[];
    date: string;
    documents: Document[];
  }
>;

const getOperationsData = (operations: Operation[]) =>
  operations.reduce((acc: any, operation: Operation) => {
    if (!operation?._id) return acc;

    const { _id, dividend } = operation;
    acc[_id] = {
      totalAmount: dividend?.totalAmount || 0,
      retentionPercentage: dividend?.retentionPercentage || 0,
      customRetentions: dividend?.customRetentions || [],
      date: operation?.date || new Date().toISOString(),
      documents: operation?.documents || [],
    };

    return acc;
  }, {} as DataByOperation);

const getDividendsData = (
  partner: Partner,
  dataByOperationId: DataByOperation
) => {
  let totalDividendsAmount = 0;

  const dividends: DividendData[] = partner.dividends.map((dividend) => {
    const opData = dataByOperationId[dividend.operation] || {
      totalAmount: 0,
      retentionPercentage: 0,
      customRetentions: [],
    };

    totalDividendsAmount += dividend.amount - dividend.retentionAmount;

    return {
      partnerId: partner._id,
      operationId: dividend.operation,
      date: opData.date,
      ndPercentage: dividend.ndPercentage,
      totalAmount: opData.totalAmount,
      amount: dividend.amount,
      retentionAmount: dividend.retentionAmount,
      retentionPercentage: dividend?.retentionPercentage || 0,
      documents: opData.documents,
    };
  });

  return { dividends, totalDividendsAmount };
};

export const useGetDividends = ({ partner }: UseGetDividendsProps) => {
  const [dividends, setDividends] = useState<DividendData[]>([]);
  const [totalDividendsAmount, setTotalDividendsAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getDividends = useCallback(async () => {
    if (!partner?.dividends) return;

    setIsLoading(true);

    try {
      const operationIds = partner.dividends.map((d) => d.operation);

      const operations = await store.dispatch(
        getOperationsByIds({ operationIds, fieldsToPopulate: ['documents'] })
      );

      const dataByOperationId = getOperationsData(operations);

      const { dividends, totalDividendsAmount } = getDividendsData(
        partner,
        dataByOperationId
      );

      setTotalDividendsAmount(totalDividendsAmount);
      setDividends(dividends);
    } catch (error) {
      console.error('Error fetching dividends:', error);
    } finally {
      setIsLoading(false);
    }
  }, [partner]);

  useEffect(() => {
    getDividends();
  }, [getDividends]);

  return { data: { dividends, totalDividendsAmount }, isLoading };
};
